import type { DateRangeExpression } from '@framework/analytics/models/date-range-expression.model';
import type { PageConfig } from '@framework/analytics/models/page.model';
import {
	coerceToDateRangeExpression,
	formatDateToISO,
	normalizeDateRangeExpression,
	tryResolveDateRangeExpression,
} from '@framework/analytics/utils/date-range.util';

/** Step-2 fallback when view `dateRange` and page `filters.date.default` are absent. */
export const REPORT_DEFAULT_DATE_RANGE_FALLBACK = [
	'lastMonth',
] as const satisfies DateRangeExpression;

export function dateRangeExpressionsEqual(
	left: DateRangeExpression | undefined,
	right: DateRangeExpression | undefined
): boolean {
	if (left === undefined && right === undefined) {
		return true;
	}
	if (left === undefined || right === undefined) {
		return false;
	}
	if (left.length !== right.length) {
		return false;
	}
	return left.every((token, index) => token === right[index]);
}

export function parseViewDateRangeFromApi(
	raw: unknown
): DateRangeExpression | undefined {
	if (raw === null || raw === undefined) {
		return undefined;
	}
	try {
		return coerceToDateRangeExpression(raw);
	} catch {
		return undefined;
	}
}

function pageFilterDefaultToDateRangeExpression(
	page: PageConfig | undefined
): DateRangeExpression | undefined {
	const rawDefault = page?.filters?.date?.default;
	if (!rawDefault || typeof rawDefault !== 'object') {
		return undefined;
	}
	const start =
		'start' in rawDefault
			? (rawDefault as { start?: string }).start?.trim()
			: undefined;
	const end =
		'end' in rawDefault
			? (rawDefault as { end?: string }).end?.trim()
			: undefined;
	if (!start || !end) {
		return undefined;
	}
	return normalizeDateRangeExpression([start, end]);
}

/**
 * Drawer + display precedence: view `dateRange` → page filter default → `lastMonth`.
 */
export function resolveReportDefaultDateRangeExpression(
	viewDateRange: DateRangeExpression | undefined,
	page: PageConfig | undefined
): DateRangeExpression {
	if (viewDateRange !== undefined) {
		return viewDateRange;
	}
	return (
		pageFilterDefaultToDateRangeExpression(page) ??
		REPORT_DEFAULT_DATE_RANGE_FALLBACK
	);
}

/** Converts a resolved expression to `getPageData` `timeDimension.dateRange` (ISO or relative tokens). */
export function dateRangeExpressionForPageData(
	expr: DateRangeExpression
): [string, string] {
	const normalized = normalizeDateRangeExpression(expr);
	if (normalized.length === 2) {
		return [normalized[0], normalized[1]];
	}
	const resolved = tryResolveDateRangeExpression(normalized);
	if (resolved) {
		return [
			formatDateToISO(resolved.startDate),
			formatDateToISO(resolved.endDate),
		];
	}
	return [normalized[0], normalized[0]];
}
